/**
 * WebSocket Real-time Event Service
 * Manages WebSocket connection, auto-reconnect, heartbeat pinging,
 * and event subscription to eliminate Short Polling.
 */

class WebSocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.reconnectTimer = null;
    this.pingTimer = null;
    this.reconnectDelay = 2000;
    this.maxReconnectDelay = 30000;
    this.url = null;
    this.userId = null;
    this.isConnected = false;
  }

  connect(url, userId) {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.url = url || 'wss://qikliik49k.execute-api.ap-northeast-2.amazonaws.com/prod';
    this.userId = userId || 'anonymous';

    const fullUrl = `${this.url}?userId=${encodeURIComponent(this.userId)}`;

    try {
      this.socket = new WebSocket(fullUrl);

      this.socket.onopen = () => {
        console.log('[WebSocket] Real-time connection established');
        this.isConnected = true;
        this.reconnectDelay = 2000;
        this.startHeartbeat();
        this.emit('connected', { isConnected: true });
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'PONG') return;
          console.log('[WebSocket Message Received]:', data);
          if (data.eventType) {
            this.emit(data.eventType, data.payload || data);
          }
        } catch (e) {
          console.warn('[WebSocket Parse Warning]:', event.data);
        }
      };

      this.socket.onerror = (err) => {
        console.warn('[WebSocket Error]:', err);
      };

      this.socket.onclose = () => {
        console.log('[WebSocket] Connection closed. Scheduling reconnect...');
        this.isConnected = false;
        this.stopHeartbeat();
        this.scheduleReconnect();
      };

    } catch (err) {
      console.warn('[WebSocket Connection Failure]:', err);
      this.scheduleReconnect();
    }
  }

  scheduleReconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      console.log(`[WebSocket] Reconnecting in ${this.reconnectDelay / 1000}s...`);
      this.connect(this.url, this.userId);
      this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, this.maxReconnectDelay);
    }, this.reconnectDelay);
  }

  startHeartbeat() {
    this.stopHeartbeat();
    this.pingTimer = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ action: 'ping' }));
      }
    }, 45000); // 45s heartbeat ping
  }

  stopHeartbeat() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType).add(callback);

    return () => this.off(eventType, callback);
  }

  off(eventType, callback) {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType).delete(callback);
    }
  }

  emit(eventType, data) {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType).forEach(cb => {
        try {
          cb(data);
        } catch (e) {
          console.error('[WebSocket Callback Error]:', e);
        }
      });
    }
  }

  disconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.stopHeartbeat();
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.isConnected = false;
  }
}

export const wsService = new WebSocketService();
export default wsService;
